/**
 * Jurisdiction context — resolves the tenant's country profile once, right
 * after the session is known and before the authenticated shell renders, and
 * hands it to every country-dependent surface.
 *
 * Deliberately mounted INSIDE the authenticated tree rather than in the auth
 * bootstrap: `refreshOnLoad()` converts anything that throws into an
 * unauthenticated snapshot, so a jurisdiction outage there would silently log
 * people out. Here a failure is just a failure.
 *
 * Three states, and the error state fails CLOSED:
 *   - `loading` — the shell waits (children are not rendered).
 *   - `loaded`  — flags are authoritative.
 *   - `error`   — core HR still renders, but every jurisdiction-dependent
 *                 surface shows a retryable "configuration unavailable"
 *                 state. We never synthesise a default country or readiness,
 *                 and an error never renders the "coming soon" fence — only a
 *                 loaded flag that is genuinely `false` does.
 */
import { createContext, useContext, useMemo, type PropsWithChildren, type ReactNode } from "react";
import { useJurisdictionQuery, type TenantJurisdiction } from "../hooks/jurisdiction";

export type JurisdictionState =
  | { status: "loading" }
  | { status: "loaded"; jurisdiction: TenantJurisdiction }
  | { status: "error"; retry: () => void };

const JurisdictionContext = createContext<JurisdictionState | null>(null);

export interface JurisdictionProviderProps extends PropsWithChildren {
  /** Tenant whose jurisdiction to resolve. */
  tenantSlug: string;
  /** Rendered instead of `children` while the first fetch is in flight. */
  loadingFallback?: ReactNode;
}

export function JurisdictionProvider({
  tenantSlug,
  loadingFallback = null,
  children,
}: JurisdictionProviderProps) {
  const query = useJurisdictionQuery(tenantSlug);
  const { data, isError, refetch } = query;

  const state = useMemo<JurisdictionState>(() => {
    if (data !== undefined) return { status: "loaded", jurisdiction: data };
    if (isError) return { status: "error", retry: () => void refetch() };
    return { status: "loading" };
  }, [data, isError, refetch]);

  return (
    <JurisdictionContext.Provider value={state}>
      {state.status === "loading" ? loadingFallback : children}
    </JurisdictionContext.Provider>
  );
}

/**
 * The full three-state value. Surfaces that must distinguish "not available
 * in this country" from "we could not find out" use this.
 */
export function useJurisdiction(): JurisdictionState {
  const state = useContext(JurisdictionContext);
  if (state === null) {
    throw new Error("useJurisdiction() requires a <JurisdictionProvider> ancestor");
  }
  return state;
}

/**
 * The jurisdiction when it is known, `null` while loading or after a failure.
 * For callers that only need to read a value and have their own fallback —
 * never for deciding whether a feature exists (that decision must not treat
 * "unknown" as "off"; use `useJurisdiction()`).
 */
export function useJurisdictionValue(): TenantJurisdiction | null {
  const state = useJurisdiction();
  return state.status === "loaded" ? state.jurisdiction : null;
}
